import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {
    COOKIE,
    COUNTRIES_CURRENCY,
    DISPATCH_COUNTRIES,
    HOME_HREF,
    WORLDWIDE_SHIPPING_HREF
} from '../constants'
import {getShipToCountry, getDefaultShipToCountry} from './ship-to-country-utils'

const NON_OUTLET_SITE_ALIASES = ['eu', 'us']
const SITE_ALIASES = [...NON_OUTLET_SITE_ALIASES, 'outlet-eu', 'outlet-us']
const PARTNER_COUNTRIES = ['TR', 'RU']
const ENGLISH_LANGUAGE = 'en'

/**
 * Returns an explicit app config when injected, otherwise falls back to PWA Kit SSR config.
 *
 * @param {object} [appConfig] - Optional app configuration supplied by tests or callers.
 * @returns {object} The app configuration used for site and locale resolution.
 */
const getAppConfig = (appConfig) => appConfig || getConfig().app

/**
 * Builds a URL base from PWA_DOMAIN_SITE for parsing relative request URLs.
 *
 * @param {object} [appConfig] - Optional app configuration supplied by tests or callers.
 * @returns {string} Absolute URL base.
 */
const getPwaDomainSiteUrl = (appConfig) => {
    const pwaDomainSite = getAppConfig(appConfig)?.PWA_DOMAIN_SITE || 'localhost:3000'

    return /^[a-zA-Z]+:\/\//.test(pwaDomainSite) ? pwaDomainSite : `https://${pwaDomainSite}`
}

/**
 * Normalizes external country values to the two-letter ISO shape used by the app.
 *
 * @param {string|null|undefined} countryCode - Country code from URL, cookie, or geo headers.
 * @returns {string|null} A normalized country code, or null when invalid/unknown.
 */
export const normalizeCountryCode = (countryCode) => {
    if (!countryCode) return null

    const normalized = String(countryCode).trim().toUpperCase()
    if (!/^[A-Z]{2}$/.test(normalized) || normalized === 'XX') return null

    return normalized === 'UK' ? 'GB' : normalized
}

/**
 * Reads an explicit delivery country from a URL query string.
 *
 * @param {string} url - Absolute or relative request URL.
 * @returns {string|null} The normalized country query value.
 */
export const resolveExplicitCountryFromUrl = (url, appConfig) => {
    try {
        const {searchParams} = new URL(url || '/', getPwaDomainSiteUrl(appConfig))
        return normalizeCountryCode(searchParams.get('country'))
    } catch (error) {
        return null
    }
}

/**
 * Checks whether a country is handled by an external partner site.
 *
 * @param {string|null|undefined} countryCode - Country code to inspect.
 * @returns {boolean} True when the country is routed to a partner site.
 */
export const isPartnerCountry = (countryCode) =>
    PARTNER_COUNTRIES.includes(normalizeCountryCode(countryCode))

/**
 * Checks whether the country exists in the SFCC ship-to country list.
 *
 * @param {string|null|undefined} countryCode - Country code to inspect.
 * @returns {boolean} True when the app can sell/ship directly to the country.
 */
export const isSupportedShipToCountry = (countryCode) => {
    const normalizedCountry = normalizeCountryCode(countryCode)
    return Boolean(
        normalizedCountry && COUNTRIES_CURRENCY.some((country) => country.id === normalizedCountry)
    )
}

/**
 * Checks whether dispatch content knows how to present the country.
 *
 * @param {string|null|undefined} countryCode - Country code to inspect.
 * @returns {boolean} True for direct ship-to, partner, or worldwide dispatch countries.
 */
export const isKnownDispatchCountry = (countryCode) => {
    const normalizedCountry = normalizeCountryCode(countryCode)
    if (!normalizedCountry) return false
    if (isSupportedShipToCountry(normalizedCountry) || isPartnerCountry(normalizedCountry)) {
        return true
    }

    return DISPATCH_COUNTRIES.some((region) =>
        region.countries.some((country) => country.code === normalizedCountry)
    )
}

/**
 * Removes the query string from a path and keeps root paths stable.
 *
 * @param {string} [path='/'] - Request path or URL path.
 * @returns {string} Pathname without query parameters.
 */
const normalizePathname = (path = '/') => {
    const pathname = path.split('?')[0] || '/'
    return pathname === '' ? '/' : pathname
}

/**
 * Splits a path into non-empty URL segments.
 *
 * @param {string} [path='/'] - Request path or URL path.
 * @returns {string[]} Path segments.
 */
const getPathSegments = (path = '/') => normalizePathname(path).split('/').filter(Boolean)

/**
 * Resolves the storefront site alias from the first URL segment.
 *
 * @param {string} [path='/'] - Request path or URL path.
 * @returns {string|null} A supported site alias, or null when the path has none.
 */
const getSiteAliasFromPath = (path = '/') => {
    const [siteAlias] = getPathSegments(path)
    return SITE_ALIASES.includes(siteAlias) ? siteAlias : null
}

/**
 * Identifies US storefront aliases, including outlet variants.
 *
 * @param {string} [siteAlias=''] - Site alias to inspect.
 * @returns {boolean} True when the alias targets a US site.
 */
const isUSSiteAlias = (siteAlias = '') => siteAlias.endsWith('us')

/**
 * Returns the default ship-to country for a site alias.
 *
 * @param {string} siteAlias - Site alias used to infer EU/US defaults.
 * @returns {object} Default ship-to country object.
 */
const getDefaultShipToCountryForSiteAlias = (siteAlias) =>
    getDefaultShipToCountry(isUSSiteAlias(siteAlias))

/**
 * Picks the target storefront alias for a country and optional outlet context.
 *
 * @param {string} countryCode - Country code used to select EU or US storefront.
 * @param {boolean} [isOutlet=false] - Whether the outlet alias should be returned.
 * @returns {string} Target site alias.
 */
const getTargetSiteAliasForCountry = (countryCode, isOutlet = false) => {
    const prefix = isOutlet ? 'outlet-' : ''
    return normalizeCountryCode(countryCode) === 'US' ? `${prefix}us` : `${prefix}eu`
}

/**
 * Resolves a PWA Kit site configuration by storefront alias.
 *
 * @param {string} siteAlias - Storefront alias from the URL or decision engine.
 * @param {object} [appConfig] - Optional app configuration.
 * @returns {object} Matching site config, or the default site when no alias matches.
 */
const getSiteByAlias = (siteAlias, appConfig) => {
    const config = getAppConfig(appConfig)
    const siteId = Object.entries(config.siteAliases || {}).find(
        ([, alias]) => alias === siteAlias
    )?.[0]

    return config.sites.find((site) => site.id === siteId) || config.sites[0]
}

/**
 * Finds a locale by either its locale id or URL alias.
 *
 * @param {object} site - Site configuration containing supported locales.
 * @param {string} localeRef - Locale id or alias.
 * @returns {object|undefined} Matching locale configuration.
 */
const getLocaleByReference = (site, localeRef) =>
    site?.l10n?.supportedLocales?.find(
        (locale) => locale.id === localeRef || locale.alias === localeRef
    )

/**
 * Resolves the active locale from the URL path.
 *
 * @param {string} path - Request path or URL path.
 * @param {object} site - Current site configuration.
 * @param {object} appConfig - App configuration for fallback locale data.
 * @returns {object|undefined} Locale configuration from the URL or default locale.
 */
const getLocaleFromPath = (path, site, appConfig) => {
    const segments = getPathSegments(path)
    const localeRef = SITE_ALIASES.includes(segments[0]) ? segments[1] : segments[0]

    return getLocaleByReference(site, localeRef || appConfig?.defaultLocale)
}

/**
 * Returns the best English locale for a site.
 *
 * @param {object} site - Site configuration containing supported locales.
 * @returns {object|undefined} English locale or site default locale.
 */
const getEnglishLocale = (site) =>
    site?.l10n?.supportedLocales?.find((locale) => locale.id.startsWith(ENGLISH_LANGUAGE)) ||
    getLocaleByReference(site, site?.l10n?.defaultLocale)

/**
 * Finds the closest supported locale from browser preferred languages.
 *
 * @param {object} site - Site configuration containing supported locales.
 * @param {string[]} [preferredLocales=[]] - Browser Accept-Language values.
 * @returns {object|null} Matching locale configuration, or null when none match.
 */
const getClosestLocale = (site, preferredLocales = []) => {
    const supportedLocales = site?.l10n?.supportedLocales || []
    const normalizedLocales = preferredLocales.filter(Boolean)

    for (const preferredLocale of normalizedLocales) {
        const exactLocale = getLocaleByReference(site, preferredLocale)
        if (exactLocale) return exactLocale

        const languageCode = String(preferredLocale).split(/[-_]/)[0]
        const languageLocale = supportedLocales.find((locale) => locale.id.startsWith(languageCode))
        if (languageLocale) return languageLocale
    }

    return null
}

/**
 * Chooses the locale used when SSR redirects a shopper to a site homepage.
 *
 * @param {object} site - Target site configuration.
 * @param {string[]} [acceptLanguages=[]] - Browser Accept-Language values.
 * @param {object|string|null} [currentLocale=null] - Locale currently present in the path.
 * @returns {object|undefined} Locale configuration for the redirect.
 */
const resolveRedirectLocale = (site, acceptLanguages = [], currentLocale = null) =>
    getClosestLocale(site, acceptLanguages) ||
    getLocaleByReference(site, currentLocale?.id || currentLocale) ||
    getEnglishLocale(site)

/**
 * Resolves a modal language suggestion from browser Accept-Language values.
 *
 * @param {object} site - Current site configuration.
 * @param {string[]} [acceptLanguages=[]] - Browser Accept-Language values.
 * @returns {object|null} Matching locale configuration, or null when none match.
 */
const resolveBrowserLocale = (site, acceptLanguages = []) => getClosestLocale(site, acceptLanguages)

/**
 * Builds a normalized home URL for a target site and locale.
 *
 * @param {string} siteAlias - Storefront alias.
 * @param {string} localeAlias - Locale URL alias.
 * @returns {string} Home URL for the target site/locale.
 */
const buildSiteHomeUrl = (siteAlias, localeAlias) => `/${siteAlias}/${localeAlias}${HOME_HREF}`

/**
 * Checks whether a path points to the global root.
 *
 * @param {string} path - Request path or URL path.
 * @returns {boolean} True for the root path.
 */
const isRootPath = (path) => normalizePathname(path) === '/'

/**
 * Checks whether a path is the worldwide shipping dispatch page.
 *
 * @param {string} path - Request path or URL path.
 * @returns {boolean} True for the worldwide shipping path.
 */
const isWorldwideShippingPath = (path) => normalizePathname(path) === WORLDWIDE_SHIPPING_HREF

/**
 * Detects site-only or site+locale paths that should redirect to a concrete home URL.
 *
 * @param {string} path - Request path or URL path.
 * @param {object} site - Current site configuration.
 * @returns {boolean} True when the path needs a home redirect.
 */
const isPartialSitePath = (path, site) => {
    const segments = getPathSegments(path)

    if (!site) return false
    if (segments.length === 1) return true
    if (segments.length === 2 && getLocaleByReference(site, segments[1])) return true

    return false
}

/**
 * Reads and normalizes a delivery-country cookie.
 *
 * @param {object} cookies - Request or browser cookie map.
 * @param {string} name - Cookie name to read.
 * @returns {string|null} Normalized cookie country.
 */
const getCookieCountry = (cookies, name) => normalizeCountryCode(cookies?.[name])

/**
 * Resolves a concrete ship-to country object for a decision.
 *
 * @param {string} countryCode - Preferred country code.
 * @param {string} siteAlias - Current or target site alias.
 * @returns {object} Ship-to country object, falling back to the site default.
 */
const getDecisionShipToCountry = (countryCode, siteAlias) =>
    getShipToCountry(countryCode, isUSSiteAlias(siteAlias)) ||
    getDefaultShipToCountryForSiteAlias(siteAlias)

/**
 * Creates the neutral decision object shared by all decision branches.
 *
 * @param {object} params - Decision inputs that must be exposed to consumers.
 * @returns {object} Mutable delivery-country decision object.
 */
const getBaseDecision = ({
    explicitCountry,
    acceptedCountry,
    contextCountry,
    geoCountry,
    geoSource,
    clientIp,
    siteAlias,
    path
}) => ({
    explicitCountry,
    acceptedCountry,
    contextCountry,
    geoCountry,
    geoSource,
    clientIp,
    siteAlias,
    path,
    shipToCountry: null,
    suggestedCountry: null,
    shouldShowCountryModal: false,
    shouldShowLocaleModal: false,
    suggestedLocale: null,
    suggestedLocaleAlias: null,
    geolocationFailed: false,
    unsupportedCountry: false,
    redirectTo: null
})

/**
 * Adds locale suggestions and modal flags to a delivery-country decision.
 *
 * @param {object} params - Locale decision inputs.
 * @param {object} params.decision - Decision object to enrich.
 * @param {object} params.site - Site configuration for locale lookup.
 * @param {string[]} params.acceptLanguages - Browser Accept-Language values.
 * @param {object|string|null} params.currentLocale - Locale currently present in the path.
 * @param {boolean} params.isDispatchPage - Whether the current page is a dispatch page.
 * @returns {object} The enriched decision object.
 */
const addLocaleDecision = ({decision, site, acceptLanguages, currentLocale, isDispatchPage}) => {
    const browserLocale = resolveBrowserLocale(site, acceptLanguages)
    const suggestedLocale =
        browserLocale ||
        getLocaleByReference(site, currentLocale?.id || currentLocale) ||
        getEnglishLocale(site)

    decision.suggestedLocale = suggestedLocale?.id || null
    decision.suggestedLocaleAlias = suggestedLocale?.alias || null

    if (
        !isDispatchPage &&
        browserLocale &&
        currentLocale &&
        browserLocale.id !== currentLocale.id
    ) {
        decision.shouldShowLocaleModal = true
    }

    return decision
}

/**
 * Builds the complete SSR delivery-country decision for the current request.
 *
 * This function is pure by design: it reads only the supplied path, cookies,
 * headers-derived geo data, and optional app config. The result tells SSR whether
 * to redirect, which ship-to country to hydrate, and whether the client should
 * ask the shopper to confirm country or language.
 *
 * @param {object} params - Delivery-country decision inputs.
 * @param {string} [params.path='/'] - Request path.
 * @param {string} [params.originalUrl=path] - Original request URL including query string.
 * @param {string} [params.explicitCountry] - Country explicitly supplied by the caller.
 * @param {string} [params.geoCountry] - Country detected from trusted geo headers.
 * @param {string|null} [params.geoSource=null] - Source of the geo country.
 * @param {string|null} [params.clientIp=null] - Trusted client IP used for diagnostics.
 * @param {string[]} [params.acceptLanguages=[]] - Browser Accept-Language values.
 * @param {object} [params.cookies={}] - Cookie map from the request.
 * @param {object} [params.appConfig] - Optional app config used by tests/callers.
 * @param {boolean} [params.useSiteDefaultWhenGeoUnavailable=false] - Local-dev fallback flag.
 * @returns {object} Delivery-country decision consumed by SSR and React hydration.
 */
export const buildDeliveryCountryDecision = ({
    path = '/',
    originalUrl = path,
    explicitCountry: providedExplicitCountry,
    geoCountry: providedGeoCountry,
    geoSource = null,
    clientIp = null,
    acceptLanguages = [],
    cookies = {},
    appConfig,
    useSiteDefaultWhenGeoUnavailable = false
}) => {
    const config = getAppConfig(appConfig)
    const explicitCountry =
        normalizeCountryCode(providedExplicitCountry) ||
        resolveExplicitCountryFromUrl(originalUrl, config)
    const acceptedCountry = getCookieCountry(cookies, COOKIE.SHIP_TO_COUNTRY_ACCEPTED)
    const contextCountry = getCookieCountry(cookies, COOKIE.SHIP_TO_COUNTRY)
    const acceptedCountryOverride =
        acceptedCountry && isSupportedShipToCountry(acceptedCountry)
            ? acceptedCountry
            : null
    const geoCountry = normalizeCountryCode(providedGeoCountry)
    const pathSiteAlias = getSiteAliasFromPath(path)
    const currentSiteAlias = pathSiteAlias || 'eu'
    const site = getSiteByAlias(currentSiteAlias, config)
    const currentLocale = getLocaleFromPath(path, site, config)
    const decision = getBaseDecision({
        explicitCountry,
        acceptedCountry,
        contextCountry,
        geoCountry,
        geoSource,
        clientIp,
        siteAlias: currentSiteAlias,
        path: normalizePathname(path)
    })
    const dispatchPage = isWorldwideShippingPath(path)

    const suppressHandledCountryModal = (nextDecision) => {
        if (
            nextDecision.shouldShowCountryModal &&
            acceptedCountry &&
            acceptedCountry === nextDecision.shipToCountry?.id
        ) {
            nextDecision.shouldShowCountryModal = false
        }

        return nextDecision
    }
    const addLocaleDecisionAndSuppressHandledCountryModal = (params) =>
        suppressHandledCountryModal(addLocaleDecision(params))
    const countryMatchesCurrentSitePath = (countryCode) =>
        !pathSiteAlias ||
        getTargetSiteAliasForCountry(countryCode, pathSiteAlias.startsWith('outlet-')) ===
            pathSiteAlias

    const applySupportedCountryOverride = (countryCode) => {
        const targetSiteAlias = isRootPath(path)
            ? getTargetSiteAliasForCountry(countryCode, false)
            : pathSiteAlias || getTargetSiteAliasForCountry(countryCode, false)
        const targetSite = getSiteByAlias(targetSiteAlias, config)
        const redirectLocale = resolveRedirectLocale(targetSite, acceptLanguages, currentLocale)

        decision.siteAlias = targetSiteAlias
        decision.shipToCountry = getDecisionShipToCountry(countryCode, targetSiteAlias)
        decision.suggestedCountry = countryCode

        if (isRootPath(path) || isPartialSitePath(path, targetSite)) {
            decision.redirectTo = buildSiteHomeUrl(targetSiteAlias, redirectLocale?.alias || 'en')
        }

        return addLocaleDecisionAndSuppressHandledCountryModal({
            decision,
            site: targetSite,
            acceptLanguages,
            currentLocale,
            isDispatchPage: false
        })
    }

    if (dispatchPage) {
        const dispatchCountry = acceptedCountryOverride
            ? acceptedCountryOverride
            : isSupportedShipToCountry(contextCountry)
            ? contextCountry
            : isSupportedShipToCountry(acceptedCountry)
            ? acceptedCountry
            : null

        decision.shipToCountry = dispatchCountry
            ? getDecisionShipToCountry(dispatchCountry, 'eu')
            : getDefaultShipToCountry(false)

        return addLocaleDecisionAndSuppressHandledCountryModal({
            decision,
            site: getSiteByAlias('eu', config),
            acceptLanguages,
            currentLocale,
            isDispatchPage: true
        })
    }

    const overrideCountry = isSupportedShipToCountry(explicitCountry) ? explicitCountry : null
    const hasInvalidExplicitCountry = Boolean(
        explicitCountry && !isSupportedShipToCountry(explicitCountry)
    )
    const geolocationFailed = !geoCountry
    const invalidProvidedGeoCountry = geolocationFailed && Boolean(geoSource)
    const unsupportedCountry = Boolean(geoCountry && !isKnownDispatchCountry(geoCountry))
    const hasAcceptedCountryForCurrentPath =
        acceptedCountryOverride && countryMatchesCurrentSitePath(acceptedCountryOverride)

    decision.geolocationFailed = geolocationFailed
    decision.unsupportedCountry =
        hasInvalidExplicitCountry ||
        (!hasAcceptedCountryForCurrentPath && (invalidProvidedGeoCountry || unsupportedCountry))

    if (hasAcceptedCountryForCurrentPath) {
        return applySupportedCountryOverride(acceptedCountryOverride)
    }

    if (hasInvalidExplicitCountry || invalidProvidedGeoCountry) {
        decision.shipToCountry = getDefaultShipToCountry(false)
        decision.redirectTo = WORLDWIDE_SHIPPING_HREF
        return decision
    }

    if (overrideCountry && isSupportedShipToCountry(overrideCountry)) {
        return applySupportedCountryOverride(overrideCountry)
    }

    if (unsupportedCountry) {
        decision.shipToCountry = getDefaultShipToCountry(false)
        decision.redirectTo = WORLDWIDE_SHIPPING_HREF
        return decision
    }

    if (geolocationFailed && useSiteDefaultWhenGeoUnavailable) {
        const targetSiteAlias = pathSiteAlias || currentSiteAlias
        const targetSite = getSiteByAlias(targetSiteAlias, config)
        const redirectLocale = resolveRedirectLocale(targetSite, acceptLanguages, currentLocale)

        decision.siteAlias = targetSiteAlias
        decision.shipToCountry = getDefaultShipToCountryForSiteAlias(targetSiteAlias)
        decision.suggestedCountry = decision.shipToCountry.id

        if (isRootPath(path) || isPartialSitePath(path, targetSite)) {
            decision.redirectTo = buildSiteHomeUrl(targetSiteAlias, redirectLocale?.alias || 'en')
        }

        return addLocaleDecisionAndSuppressHandledCountryModal({
            decision,
            site: targetSite,
            acceptLanguages,
            currentLocale,
            isDispatchPage: false
        })
    }

    if (geolocationFailed) {
        decision.shipToCountry = getDefaultShipToCountry(false)
        decision.redirectTo = WORLDWIDE_SHIPPING_HREF
        return decision
    }

    const isPartnerGeoCountry = isPartnerCountry(geoCountry)

    if (isRootPath(path)) {
        const targetSiteAlias = isPartnerGeoCountry
            ? 'eu'
            : getTargetSiteAliasForCountry(geoCountry, false)
        const targetSite = getSiteByAlias(targetSiteAlias, config)
        const redirectLocale = resolveRedirectLocale(targetSite, acceptLanguages, currentLocale)

        decision.siteAlias = targetSiteAlias
        decision.shipToCountry = isPartnerGeoCountry
            ? getDefaultShipToCountryForSiteAlias(targetSiteAlias)
            : getDecisionShipToCountry(geoCountry, targetSiteAlias)
        decision.suggestedCountry = geoCountry
        decision.shouldShowCountryModal = isPartnerGeoCountry
        decision.redirectTo = buildSiteHomeUrl(targetSiteAlias, redirectLocale?.alias || 'en')

        return addLocaleDecisionAndSuppressHandledCountryModal({
            decision,
            site: targetSite,
            acceptLanguages,
            currentLocale,
            isDispatchPage: false
        })
    }

    const targetSiteAlias = pathSiteAlias || currentSiteAlias
    const targetSite = getSiteByAlias(targetSiteAlias, config)
    const isUSSite = isUSSiteAlias(targetSiteAlias)
    const isUSGeoCountry = geoCountry === 'US'

    decision.siteAlias = targetSiteAlias
    decision.suggestedCountry = geoCountry

    if (isPartnerGeoCountry) {
        decision.shipToCountry = getDefaultShipToCountryForSiteAlias(targetSiteAlias)
        decision.shouldShowCountryModal = true
    } else if (isUSSite) {
        decision.shipToCountry = getDefaultShipToCountry(true)
        decision.shouldShowCountryModal = !isUSGeoCountry
    } else if (isUSGeoCountry) {
        decision.shipToCountry = getDefaultShipToCountry(false)
        decision.shouldShowCountryModal = true
    } else {
        decision.shipToCountry = getDecisionShipToCountry(geoCountry, targetSiteAlias)
    }

    if (isPartialSitePath(path, targetSite)) {
        const redirectLocale = resolveRedirectLocale(targetSite, acceptLanguages, currentLocale)
        decision.redirectTo = buildSiteHomeUrl(targetSiteAlias, redirectLocale?.alias || 'en')
    }

    return addLocaleDecisionAndSuppressHandledCountryModal({
        decision,
        site: targetSite,
        acceptLanguages,
        currentLocale,
        isDispatchPage: false
    })
}

/**
 * Determines whether the delivery-country middleware should ignore a request.
 *
 * Non-page requests, API routes, callbacks, static assets, and non-GET/HEAD
 * methods bypass dispatch so the middleware stays scoped to storefront routing.
 *
 * @param {object} req - Express request object.
 * @returns {boolean} True when the request should bypass delivery dispatch.
 */
export const shouldSkipDeliveryCountryDispatch = (req) => {
    const method = req.method?.toUpperCase()
    const path = normalizePathname(req.path || req.url || '/')

    if (!['GET', 'HEAD'].includes(method)) return true
    if (
        path.startsWith('/api') ||
        path.startsWith('/mobify') ||
        path.startsWith('/callback') ||
        path.startsWith('/ocapi') ||
        path.startsWith('/sfcc') ||
        path.startsWith('/static') ||
        path.startsWith('/favicon') ||
        path.startsWith('/worker') ||
        path.startsWith('/user-agent') ||
        path.startsWith('/block-status') ||
        path.startsWith('/ip') ||
        path.startsWith('/validate-recaptcha') ||
        path.startsWith('/csp-reporting') ||
        path.includes('/oauth2/jwks')
    ) {
        return true
    }

    return /\.(?:js|css|map|json|png|jpg|jpeg|gif|webp|avif|svg|ico|woff2?|ttf|otf|txt|xml)$/i.test(
        path
    )
}

/**
 * Reads the shopper-context ship-to country cookie.
 *
 * @param {object} [cookies={}] - Cookie map from SSR locals or the browser.
 * @returns {string|null} Normalized cookie country.
 */
export const getContextCookieCountry = (cookies = {}) =>
    getCookieCountry(cookies, COOKIE.SHIP_TO_COUNTRY)

/**
 * Reads the accepted ship-to country cookie set after shopper confirmation.
 *
 * @param {object} [cookies={}] - Cookie map from SSR locals or the browser.
 * @returns {string|null} Normalized accepted country.
 */
export const getAcceptedCookieCountry = (cookies = {}) =>
    getCookieCountry(cookies, COOKIE.SHIP_TO_COUNTRY_ACCEPTED)
